import { store } from '@redux/store';
import { selectConfigurationVwoSdkKey, selectConfigurationVwoAccountId } from 'layout/redux/selectors';

// Singleton promise — VWO client is initialized only once per page load.
let clientPromise = null;

const getClient = () => {
  if (!clientPromise) {
    const state = store.getState();
    const sdkKey = selectConfigurationVwoSdkKey(state);
    const accountId = selectConfigurationVwoAccountId(state);

    if (!sdkKey || !accountId) {
      // returning a promise of null if we don't have keys right now
      // we may in the future though (as we get them from an async request)
      return Promise.resolve(null);
    }

    // Lazy-load the VWO FME SDK — do NOT change this back to a static top-level import.
    // The SDK ships untranspiled (no babel-loader in either webpack config); imported
    // eagerly it lands in the shared initial `vendors` chunk, so any syntax feature it
    // uses that an old browser (iPadOS/Safari 14) can't parse takes down the whole chunk
    // and every unrelated feature on the page. Its own async chunk isolates that risk.
    // Full analysis: PR #7261.
    clientPromise = import(/* webpackChunkName: "vwo-fme-sdk" */ 'vwo-fme-node-sdk')
      .then(({ init }) => init({ accountId: String(accountId), sdkKey }))
      .catch(() => {
        // Clear the singleton so a later event can retry (e.g. a transient chunk-load
        // failure). On browsers where the SDK genuinely can't run, each subsequent call
        // simply re-fails and no-ops — trackEvent fires rarely, so there is no retry storm.
        clientPromise = null;
        return null;
      });
  }
  return clientPromise;
};

/**
 * Tracks a VWO FME event for the given user.
 * @param {string} eventName - The VWO event name configured in the FME dashboard.
 * @param {string} userId - The user identifier (e.g. Segment anonymous ID).
 * @param {Object} [properties] - Optional event properties.
 */
export const trackEvent = async (eventName, userId, properties) => {
  if (!userId) { return; }
  const client = await getClient();
  if (!client) { return; }
  client.trackEvent(eventName, { id: userId }, properties);
};

export { getClient as getVwoClient };
